import React from 'react';
import { motion } from 'framer-motion';
import { Gift, Check, ShieldAlert, Sparkles, Lock, Zap, ShieldCheck, Tag, Star } from 'lucide-react';
import { FadeIn, SectionDivider, CtaButton } from '../ui';
const bonus1Features = ['Checklist completo de preparación de espacio, tiempo y estado mental', 'Hoja de intención para centrarte en minutos antes de empezar', 'Mini planner para organizar tus 3 noches sin improvisar nada', 'Rutina completa de preparación en solo 10 minutos', 'Protocolo de reset emocional para entrar con calma a cada noche', 'Checklist final imprimible para las 3 noches'];
const bonus2Features = ['La regla de oro que debes aplicar inmediatamente después de terminar', 'Lista exacta de lo que debes evitar en los primeros 7 días', 'Cómo identificar señales internas y externas sin exagerar ni dramatizar', 'Tracker de 7 días para registrar lo que observas con claridad', 'Hoja de autocontrol para no actuar desde la ansiedad y el miedo', 'Protocolo de 3 minutos para recuperar tu calma cuando más la necesitas', 'Checklist imprimible de qué hacer y qué evitar cada día'];
interface BonusCardProps {
  number: number;
  image: string;
  title: string;
  subtitle: string;
  intro: React.ReactNode;
  features: string[];
  reverse?: boolean;
}
const BonusCard = ({
  number,
  image,
  title,
  subtitle,
  intro,
  features,
  reverse = false
}: BonusCardProps) => <FadeIn data-id="element-10">
    <div className="glass-panel rounded-2xl overflow-hidden border-gold/25 relative" data-id="element-11">
      {/* Gold accent line */}
      <div className="absolute top-0 left-0 w-full h-[2px] bg-gradient-to-r from-transparent via-gold to-transparent" data-id="element-12"></div>

      <div className={`grid grid-cols-1 lg:grid-cols-5 gap-0 ${reverse ? 'lg:[direction:rtl]' : ''}`} data-id="element-13">
        {/* Image */}
        <div className="lg:col-span-2 relative bg-mystic-900/60 p-6 md:p-8 flex items-center justify-center [direction:ltr]" data-id="element-14">
          <div className="absolute inset-0 bg-gold/10 blur-[80px] opacity-50" data-id="element-15"></div>
          <motion.img whileHover={{
          scale: 1.03,
          rotate: -1
        }} transition={{
          duration: 0.4
        }} src={image} alt={title} className="relative z-10 w-full max-w-[280px] h-auto rounded-lg shadow-[0_20px_50px_rgba(0,0,0,0.6)] border border-gold/20" data-id="element-16" />
        </div>

        {/* Content */}
        <div className="lg:col-span-3 p-8 md:p-10 [direction:ltr]" data-id="element-17">
          <div className="flex items-center gap-3 mb-4" data-id="element-18">
            <div className="flex items-center gap-2 px-3 py-1 rounded-full bg-gold/15 border border-gold/40 text-gold text-xs font-bold tracking-widest uppercase" data-id="element-19">
              <Gift size={14} data-id="element-20" />
              Bono #{number} — Gratis hoy
            </div>
          </div>

          <h3 className="text-2xl md:text-3xl font-serif font-bold text-white mb-2 leading-tight" data-id="element-21">
            {title}
          </h3>
          <p className="text-gold italic font-serif text-lg mb-6" data-id="element-22">{subtitle}</p>

          <div className="text-text-secondary leading-relaxed mb-6 space-y-3" data-id="element-23">
            {intro}
          </div>

          <div className="border-t border-gold/15 pt-6 mb-6" data-id="element-24">
            <p className="text-sm uppercase tracking-widest text-gold/80 font-bold mb-4" data-id="element-25">
              Lo que incluye:
            </p>
            <ul className="space-y-3" data-id="element-26">
              {features.map((feature, idx) => <li key={idx} className="flex gap-3 items-start" data-id="element-27">
                  <div className="shrink-0 mt-0.5 text-gold bg-gold/10 rounded-full p-0.5" data-id="element-28">
                    <Check size={14} strokeWidth={3} data-id="element-29" />
                  </div>
                  <span className="text-text-primary text-sm md:text-base" data-id="element-30">
                    {feature}
                  </span>
                </li>)}
            </ul>
          </div>

          {/* Value strip */}
          <div className="flex flex-wrap items-center gap-3 p-4 rounded-lg bg-mystic-900/70 border border-gold/20" data-id="element-31">
            <div className="flex items-center gap-2" data-id="element-32">
              <Tag size={16} className="text-text-secondary" data-id="element-33" />
              <span className="text-text-secondary" data-id="element-34">Valor real:</span>
              <span className="text-text-secondary line-through" data-id="element-35">$57 USD</span>
            </div>
            <div className="h-4 w-px bg-gold/20 hidden sm:block" data-id="element-36"></div>
            <div className="flex items-center gap-2 text-gold font-bold" data-id="element-37">
              <Sparkles size={16} data-id="element-38" />
              <span data-id="element-39">HOY LO RECIBES GRATIS</span>
            </div>
          </div>
          <p className="text-xs text-text-secondary/70 mt-2 italic" data-id="element-40">
            Incluido automáticamente con tu acceso.
          </p>
        </div>
      </div>
    </div>
  </FadeIn>;
export const BonusSection = () => {
  return <section className="py-20 px-4 relative bg-mystic-900" data-id="element-41">
      <div className="absolute inset-0 bg-radial-glow opacity-30 pointer-events-none" data-id="element-42"></div>

      <div className="max-w-5xl mx-auto relative z-10" data-id="element-43">
        {/* Header */}
        <FadeIn data-id="element-44">
          <div className="text-center mb-16" data-id="element-45">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-gold/30 bg-mystic-800/80 text-gold text-xs font-bold tracking-widest uppercase mb-6" data-id="element-46">
              <Gift size={14} data-id="element-47" />
              Bonos Exclusivos
              <Gift size={14} data-id="element-48" />
            </div>

            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6 leading-tight" data-id="element-49">
              Solo por hoy esto también es tuyo
            </h2>

            <div className="max-w-3xl mx-auto text-text-secondary space-y-4 text-lg" data-id="element-50">
              <p data-id="element-51">Pero eso no es todo lo que recibes.</p>
              <p data-id="element-52">
                Porque empezar bien y terminar bien son dos partes del mismo
                proceso. Y hoy quiero asegurarme de que tengas las dos.
              </p>
              <p className="text-text-primary" data-id="element-53">
                Cuando alguien compra el Amarre Ancestral de 3 Noches, la
                mayoría comete dos errores que lo arruinan todo.
              </p>
              <p data-id="element-54">
                <span className="text-burgundy font-bold" data-id="element-55">El primero:</span>{' '}
                empezar sin prepararse. Con ansiedad, sin orden, sin intención
                clara. Improvisando todo desde el inicio.
              </p>
              <p data-id="element-56">
                <span className="text-burgundy font-bold" data-id="element-57">El segundo:</span>{' '}
                terminar las 3 noches y entrar en pánico. Revisar el teléfono
                cada cinco minutos. Escribir por impulso. Y destruir con
                desesperación lo que construyó con intención.
              </p>
              <p className="text-gold font-serif italic text-xl pt-4" data-id="element-58">
                Para que tú no cometas ninguno de esos dos errores, hoy incluyo
                2 bonos completamente gratis.
              </p>
            </div>
          </div>
        </FadeIn>

        {/* Bonus Cards */}
        <div className="space-y-12" data-id="element-59">
          <BonusCard number={1} image="https://cdn.magicpatterns.com/uploads/anYNVnLBQAoqyGGwrbf4pU/IMG_BONO1.png" title="Kit de Preparación de las 3 Noches" subtitle="Orden, enfoque y claridad antes de empezar" intro={<>
                <p data-id="element-61">
                  Este bono existe para que llegues a tu primera noche con todo
                  preparado, la mente clara y cero improvisación.
                </p>
                <p data-id="element-62">
                  Sin apuros de último minuto. Sin distracciones. Sin caos
                  mental antes de comenzar.
                </p>
              </>} features={bonus1Features} data-id="element-60" />

          <BonusCard number={2} image="https://cdn.magicpatterns.com/uploads/9vXxor5383J1bnykk3dKZz/IMG_BONO2.png" title="Guía de Señales y Calma Post-Proceso" subtitle="Qué observar, qué evitar y cómo mantenerte firme después de completar tus 3 noches" intro={<>
                <p data-id="element-64">
                  Terminar las 3 noches es solo la mitad. La otra mitad es lo
                  que haces después.
                </p>
                <p data-id="element-65">
                  Y la mayoría lo arruina por una sola razón:{' '}
                  <span className="text-burgundy font-bold" data-id="element-66">la ansiedad.</span>
                </p>
                <p data-id="element-67">
                  Revisar el teléfono cada cinco minutos. Escribir por impulso.
                  Interpretar el silencio como fracaso. Forzar contacto cuando
                  deberían esperar.
                </p>
                <p data-id="element-68">
                  Esta guía existe para que no destruyas con impulsividad lo que
                  construiste con intención durante tus 3 noches.
                </p>
              </>} features={bonus2Features} reverse data-id="element-63" />
        </div>

        <SectionDivider data-id="element-69" />

        {/* Value Stack */}
        <FadeIn data-id="element-70">
          <div className="max-w-3xl mx-auto" data-id="element-71">
            <h3 className="text-2xl md:text-3xl font-bold text-center text-white mb-8" data-id="element-72">
              Todo lo que recibes hoy:
            </h3>

            <div className="glass-panel rounded-2xl p-8 md:p-10 border-gold/30 space-y-4" data-id="element-73">
              <div className="flex items-start gap-4 pb-4 border-b border-gold/15" data-id="element-74">
                <div className="shrink-0 mt-1 text-gold bg-gold/10 rounded-full p-1.5" data-id="element-75">
                  <Check size={16} strokeWidth={3} data-id="element-76" />
                </div>
                <div className="flex-1 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1" data-id="element-77">
                  <p className="text-white font-medium" data-id="element-78">
                    Amarre Ancestral de 3 Noches
                  </p>
                  <p className="text-gold font-bold" data-id="element-79">Valor: $97 USD</p>
                </div>
              </div>

              <div className="flex items-start gap-4 pb-4 border-b border-gold/15" data-id="element-80">
                <div className="shrink-0 mt-1 text-gold bg-gold/10 rounded-full p-1.5" data-id="element-81">
                  <Gift size={16} data-id="element-82" />
                </div>
                <div className="flex-1 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1" data-id="element-83">
                  <p className="text-white font-medium" data-id="element-84">
                    Bono #1 — Kit de Preparación de las 3 Noches
                  </p>
                  <p className="text-gold font-bold" data-id="element-85">Valor: $57 USD</p>
                </div>
              </div>

              <div className="flex items-start gap-4 pb-4 border-b border-gold/15" data-id="element-86">
                <div className="shrink-0 mt-1 text-gold bg-gold/10 rounded-full p-1.5" data-id="element-87">
                  <Gift size={16} data-id="element-88" />
                </div>
                <div className="flex-1 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-1" data-id="element-89">
                  <p className="text-white font-medium" data-id="element-90">
                    Bono #2 — Guía de Señales y Calma Post-Proceso
                  </p>
                  <p className="text-gold font-bold" data-id="element-91">Valor: $57 USD</p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-2" data-id="element-92">
                <p className="text-lg text-text-secondary uppercase tracking-widest" data-id="element-93">
                  Valor total:
                </p>
                <p className="text-2xl md:text-3xl font-bold text-text-secondary line-through" data-id="element-94">
                  $211 USD
                </p>
              </div>

              <div className="text-center pt-6 mt-4 border-t border-gold/30" data-id="element-95">
                <p className="text-text-secondary mb-2 uppercase tracking-widest text-sm" data-id="element-96">
                  Hoy todo por solo
                </p>
                <p className="text-5xl md:text-7xl font-bold text-gold drop-shadow-[0_0_20px_rgba(200,168,75,0.4)]" data-id="element-97">
                  $12 USD
                </p>
                <p className="text-burgundy font-bold mt-4 text-lg" data-id="element-98">
                  Estás ahorrando $199 USD hoy.
                </p>
                <p className="text-text-secondary text-sm mt-1" data-id="element-99">
                  Una sola vez. Sin suscripciones. Sin costos ocultos.
                </p>
              </div>
            </div>
          </div>
        </FadeIn>

        {/* Urgency Warning */}
        <FadeIn delay={0.1} data-id="element-100">
          <div className="max-w-3xl mx-auto mt-10" data-id="element-101">
            <div className="bg-burgundy/15 border border-burgundy/60 rounded-xl p-6 md:p-8 flex items-start gap-4" data-id="element-102">
              <ShieldAlert className="text-burgundy shrink-0 animate-pulse-slow" size={32} data-id="element-103" />
              <div className="text-text-primary" data-id="element-104">
                <p className="font-bold mb-2 uppercase tracking-wider text-white" data-id="element-105">
                  Disponibilidad limitada
                </p>
                <p className="mb-2" data-id="element-106">
                  Estos 2 bonos solo están disponibles con esta oferta de
                  lanzamiento. No sabemos hasta cuándo los incluiremos de forma
                  gratuita.
                </p>
                <p className="text-text-secondary text-sm" data-id="element-107">
                  Si cierras esta página sin acceder es posible que los
                  encuentres por separado a su precio real de $57 USD cada uno.
                </p>
                <p className="text-gold font-bold mt-3" data-id="element-108">
                  Hoy los recibes gratis. Solo hoy.
                </p>
              </div>
            </div>
          </div>
        </FadeIn>

        {/* Closing reinforcement + CTA */}
        <FadeIn delay={0.2} data-id="element-109">
          <div className="max-w-3xl mx-auto mt-16 text-center" data-id="element-110">
            <p className="text-2xl md:text-3xl font-serif text-white italic mb-4" data-id="element-111">
              Todo esto por solo $12 USD.
            </p>
            <div className="text-text-secondary space-y-2 mb-10 max-w-2xl mx-auto" data-id="element-112">
              <p data-id="element-113">
                El método completo de las 3 noches. El kit para empezar sin
                caos. La guía para terminar sin sabotearte.
              </p>
              <p data-id="element-114">
                Todo lo que necesitas para hacerlo bien. Sin intermediarios. Sin
                errores evitables. Sin ansiedad que rompa lo que construiste.
              </p>
              <p className="text-gold font-bold pt-2" data-id="element-115">
                $12 USD. Una sola vez. Acceso inmediato.
              </p>
            </div>

            <CtaButton data-id="element-116">→ QUIERO TODO ESTO POR $12 USD</CtaButton>

            <div className="flex flex-wrap justify-center gap-x-6 gap-y-3 text-sm text-text-secondary mt-8" data-id="element-117">
              <span className="flex items-center gap-2" data-id="element-118">
                <Lock size={14} className="text-gold/70" data-id="element-119" /> Pago seguro
              </span>
              <span className="flex items-center gap-2" data-id="element-120">
                <Zap size={14} className="text-gold/70" data-id="element-121" /> Acceso inmediato
              </span>
              <span className="flex items-center gap-2" data-id="element-122">
                <ShieldCheck size={14} className="text-gold/70" data-id="element-123" /> Garantía 7
                días
              </span>
              <span className="flex items-center gap-2" data-id="element-124">
                <Gift size={14} className="text-gold/70" data-id="element-125" /> 2 bonos incluidos
                gratis
              </span>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
};